import { useEffect, useState, useCallback, useMemo } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Plus, Handshake, PencilSimple, Trash, MagnifyingGlass, UsersThree, User as UserIcon,
} from "@phosphor-icons/react";

function formatDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  } catch { return "—"; }
}

const emptyForm = {
  name: "", contact_name: "", contact_email: "", contact_phone: "",
  deal_terms: "", is_active: true,
};

export default function Partners() {
  const { currentOrg } = useAuth();
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/partners");
      setPartners(data);
    } catch (err) {
      if (err?.response?.status === 403) {
        toast.error("Partners isn't enabled for this workspace");
      } else {
        toast.error("Could not load partners");
      }
      setPartners([]);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load, currentOrg?.org_id]);

  const filtered = useMemo(() => {
    if (!query.trim()) return partners;
    const q = query.toLowerCase();
    return partners.filter((p) =>
      (p.name || "").toLowerCase().includes(q) ||
      (p.contact_name || "").toLowerCase().includes(q) ||
      (p.contact_email || "").toLowerCase().includes(q)
    );
  }, [partners, query]);

  const openCreate = () => { setEditing(null); setForm(emptyForm); setDialogOpen(true); };
  const openEdit = (p) => {
    setEditing(p.partner_id);
    setForm({
      name: p.name || "",
      contact_name: p.contact_name || "",
      contact_email: p.contact_email || "",
      contact_phone: p.contact_phone || "",
      deal_terms: p.deal_terms || "",
      is_active: p.is_active !== false,
    });
    setDialogOpen(true);
  };

  const save = async () => {
    const name = form.name.trim();
    if (!name) { toast.error("Name is required"); return; }
    const payload = {
      name,
      contact_name: form.contact_name,
      contact_email: form.contact_email,
      contact_phone: form.contact_phone,
      deal_terms: form.deal_terms,
      is_active: !!form.is_active,
    };
    setSaving(true);
    try {
      if (editing) {
        const { data } = await api.patch(`/partners/${editing}`, payload);
        setPartners((all) => all.map((x) => x.partner_id === editing ? data : x));
        toast.success("Partner updated");
      } else {
        const { data } = await api.post("/partners", payload);
        setPartners((all) => [data, ...all]);
        toast.success("Partner added");
      }
      setDialogOpen(false); setForm(emptyForm); setEditing(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const doDelete = async () => {
    if (!confirmDelete) return;
    try {
      await api.delete(`/partners/${confirmDelete.partner_id}`);
      setPartners((all) => all.filter((x) => x.partner_id !== confirmDelete.partner_id));
      toast.success(`Removed ${confirmDelete.name}`);
    } catch { toast.error("Could not remove"); }
    finally { setConfirmDelete(null); }
  };

  return (
    <div className="p-8 md:p-12 max-w-[1400px]" data-testid="partners-page">
      <div className="flex items-start justify-between gap-6 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.24em] text-ink-secondary">Workspace</div>
          <h1 className="mt-2 font-display text-4xl md:text-5xl font-light tracking-tight">Partners</h1>
          <p className="mt-3 text-sm text-ink-secondary max-w-lg">
            Referral sources, resellers and channel partners sending leads your way. Attribute leads and customers to a partner to see who's driving the pipeline.
          </p>
        </div>
        <button
          data-testid="partners-add"
          onClick={openCreate}
          className="inline-flex items-center gap-2 rounded-full bg-white text-graphite-950 px-5 py-2.5 text-sm font-medium hover:bg-neutral-200 transition-colors"
        >
          <Plus weight="bold" className="h-4 w-4" />
          Add partner
        </button>
      </div>

      <div className="mt-6 relative max-w-md">
        <MagnifyingGlass weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-secondary" />
        <input
          data-testid="partners-search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search by partner or contact name"
          className="w-full bg-graphite-800 border border-white/10 rounded-md pl-10 pr-3 py-2 text-sm text-ink-primary placeholder:text-ink-secondary/60 focus:outline-none focus:border-white/30 focus:ring-1 focus:ring-white/30"
        />
      </div>

      <div className="mt-6 rounded-md border border-white/10 bg-graphite-900 overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-sm text-ink-secondary">Loading partners…</div>
        ) : filtered.length === 0 ? (
          <div className="p-16 text-center" data-testid="partners-empty">
            <Handshake weight="duotone" className="mx-auto h-10 w-10 text-ink-secondary/50" />
            <div className="mt-4 font-display text-xl tracking-tight">
              {partners.length === 0 ? "No partners yet" : "No matches"}
            </div>
            <p className="mt-2 text-sm text-ink-secondary max-w-sm mx-auto">
              {partners.length === 0
                ? "Add your first referral source or channel partner to start tracking attribution."
                : "Try a different search term."}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="partners-table">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-[0.18em] text-ink-secondary border-b border-white/10 bg-graphite-950/40">
                  <th className="px-5 py-3 font-medium">Name</th>
                  <th className="px-5 py-3 font-medium">Contact</th>
                  <th className="px-5 py-3 font-medium">Deal terms</th>
                  <th className="px-5 py-3 font-medium text-right">Leads</th>
                  <th className="px-5 py-3 font-medium text-right">Customers</th>
                  <th className="px-5 py-3 font-medium">Status</th>
                  <th className="px-5 py-3 font-medium">Added</th>
                  <th className="px-5 py-3 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {filtered.map((p) => (
                  <tr key={p.partner_id} data-testid={`partner-row-${p.partner_id}`} className="hover:bg-white/[0.02]">
                    <td className="px-5 py-3">
                      <div className="text-ink-primary" data-testid={`partner-name-${p.partner_id}`}>{p.name}</div>
                    </td>
                    <td className="px-5 py-3 text-ink-secondary">
                      <div className="flex flex-col gap-0.5">
                        {p.contact_name && <span className="text-ink-primary text-xs inline-flex items-center gap-1"><UserIcon weight="duotone" className="h-3 w-3" />{p.contact_name}</span>}
                        {p.contact_email && <span className="text-[11px]">{p.contact_email}</span>}
                        {p.contact_phone && <span className="text-[11px]">{p.contact_phone}</span>}
                        {!p.contact_name && !p.contact_email && !p.contact_phone && "—"}
                      </div>
                    </td>
                    <td className="px-5 py-3 text-ink-secondary max-w-md">
                      <div className="line-clamp-2 text-xs">{p.deal_terms || "—"}</div>
                    </td>
                    <td className="px-5 py-3 text-right" data-testid={`partner-leads-${p.partner_id}`}>
                      {(p.lead_count ?? 0) > 0 ? (
                        <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border border-blue-400/25 bg-blue-400/5 text-blue-200">
                          <UsersThree weight="duotone" className="h-3 w-3" />
                          {p.lead_count}
                        </span>
                      ) : <span className="text-ink-secondary/60 text-xs">0</span>}
                    </td>
                    <td className="px-5 py-3 text-right" data-testid={`partner-customers-${p.partner_id}`}>
                      {(p.customer_count ?? 0) > 0 ? (
                        <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border border-emerald-400/25 bg-emerald-400/5 text-emerald-200">
                          <UserIcon weight="duotone" className="h-3 w-3" />
                          {p.customer_count}
                        </span>
                      ) : <span className="text-ink-secondary/60 text-xs">0</span>}
                    </td>
                    <td className="px-5 py-3">
                      <span
                        data-testid={`partner-status-${p.partner_id}`}
                        className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border ${p.is_active ? "border-emerald-400/30 bg-emerald-400/5 text-emerald-200" : "border-white/10 text-ink-secondary"}`}
                      >
                        {p.is_active ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-ink-secondary">{formatDate(p.created_at)}</td>
                    <td className="px-5 py-3 text-right">
                      <div className="inline-flex items-center gap-1">
                        <button
                          data-testid={`partner-edit-${p.partner_id}`}
                          onClick={() => openEdit(p)}
                          className="p-2 rounded-md text-ink-secondary hover:text-ink-primary hover:bg-white/5"
                          title="Edit"
                        >
                          <PencilSimple weight="bold" className="h-3.5 w-3.5" />
                        </button>
                        <button
                          data-testid={`partner-delete-${p.partner_id}`}
                          onClick={() => setConfirmDelete(p)}
                          className="p-2 rounded-md text-ink-secondary hover:text-accent-danger hover:bg-white/5"
                          title="Remove"
                        >
                          <Trash weight="bold" className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create / edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={(v) => { if (!saving) { setDialogOpen(v); if (!v) { setForm(emptyForm); setEditing(null); } } }}>
        <DialogContent data-testid="partner-dialog" className="max-w-2xl bg-graphite-900 border-white/10 text-ink-primary">
          <DialogHeader>
            <DialogTitle className="font-display text-xl tracking-tight">
              {editing ? "Edit partner" : "Add partner"}
            </DialogTitle>
            <DialogDescription className="text-ink-secondary text-sm">
              A referral source, reseller or channel partner. Attach the arrangement in "Deal terms" — freeform notes for your own reference.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5 sm:col-span-2">
              <label className="text-xs uppercase tracking-[0.15em] text-ink-secondary">Partner name</label>
              <Input data-testid="partner-form-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} maxLength={200} placeholder="e.g. Acme Studio" className="bg-graphite-950 border-white/10 text-ink-primary" />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs uppercase tracking-[0.15em] text-ink-secondary">Contact name</label>
              <Input data-testid="partner-form-contact-name" value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} className="bg-graphite-950 border-white/10 text-ink-primary" />
            </div>
            <div className="space-y-1.5 flex flex-col justify-end">
              <label className="text-xs uppercase tracking-[0.15em] text-ink-secondary">Status</label>
              <div className="flex items-center gap-3 h-10">
                <Switch
                  data-testid="partner-form-active"
                  checked={!!form.is_active}
                  onCheckedChange={(v) => setForm({ ...form, is_active: v })}
                />
                <span className={`text-xs ${form.is_active ? "text-emerald-300/90" : "text-ink-secondary"}`}>
                  {form.is_active ? "Active" : "Inactive — hidden from dropdowns"}
                </span>
              </div>
            </div>
            <div className="space-y-1.5">
              <label className="text-xs uppercase tracking-[0.15em] text-ink-secondary">Contact email</label>
              <Input data-testid="partner-form-contact-email" type="email" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} className="bg-graphite-950 border-white/10 text-ink-primary" />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs uppercase tracking-[0.15em] text-ink-secondary">Contact phone</label>
              <Input data-testid="partner-form-contact-phone" value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} className="bg-graphite-950 border-white/10 text-ink-primary" />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <label className="text-xs uppercase tracking-[0.15em] text-ink-secondary">Deal terms <span className="opacity-60">(freeform)</span></label>
              <Textarea
                data-testid="partner-form-deal-terms"
                value={form.deal_terms}
                onChange={(e) => setForm({ ...form, deal_terms: e.target.value })}
                rows={4}
                placeholder="e.g. 15% commission on first-year revenue, paid quarterly. Deals routed via their internal team."
                className="bg-graphite-950 border-white/10 text-ink-primary"
              />
              <p className="text-[11px] text-ink-secondary/60">
                Notes for your own reference. Emergent doesn't calculate commissions or manage payouts — track those in your finance tool of choice.
              </p>
            </div>
            {editing && (
              <div className="sm:col-span-2 rounded-md border border-white/10 bg-graphite-950/40 p-3 text-xs text-ink-secondary">
                <div className="flex items-center gap-4">
                  <span className="inline-flex items-center gap-1"><UsersThree weight="duotone" className="h-3.5 w-3.5" /> Leads attributed: <span className="text-ink-primary">{partners.find((x) => x.partner_id === editing)?.lead_count ?? 0}</span></span>
                  <span className="inline-flex items-center gap-1"><UserIcon weight="duotone" className="h-3.5 w-3.5" /> Customers attributed: <span className="text-ink-primary">{partners.find((x) => x.partner_id === editing)?.customer_count ?? 0}</span></span>
                </div>
              </div>
            )}
          </div>
          <DialogFooter className="gap-2">
            <button
              data-testid="partner-form-cancel"
              onClick={() => { if (!saving) { setDialogOpen(false); setForm(emptyForm); setEditing(null); } }}
              disabled={saving}
              className="rounded-full border border-white/10 px-4 py-2 text-sm text-ink-secondary hover:text-ink-primary hover:bg-white/5 disabled:opacity-60"
            >
              Cancel
            </button>
            <button
              data-testid="partner-form-save"
              onClick={save}
              disabled={saving || !form.name.trim()}
              className="rounded-full bg-white text-graphite-950 px-4 py-2 text-sm font-medium hover:bg-neutral-100 disabled:opacity-60"
            >
              {saving ? "Saving…" : editing ? "Save changes" : "Add partner"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmDelete} onOpenChange={(v) => { if (!v) setConfirmDelete(null); }}>
        <AlertDialogContent data-testid="partner-delete-dialog" className="bg-graphite-900 border-white/10 text-ink-primary">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove partner?</AlertDialogTitle>
            <AlertDialogDescription className="text-ink-secondary">
              {confirmDelete?.name} will be soft-deleted. Leads and customers attributed to them keep the record on their side (partner_id remains for historical attribution). To stop new attributions without losing data, deactivate instead.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="partner-delete-cancel" className="bg-graphite-800 border-white/10 text-ink-primary hover:bg-white/5">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="partner-delete-confirm" onClick={doDelete} className="bg-red-400/90 text-graphite-950 hover:bg-red-300">Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
